import { useCallback, useMemo } from 'react';
import { Clock, CheckCircle2, ListChecks, AlertOctagon, RefreshCcw, type LucideIcon } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { KPICard } from '@/components/ui/KPICard';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { TooltipProvider } from '@/components/ui/Tooltip';
import { api } from '@/lib/api';
import { TaskBoard } from '@/components/raios/tasks/TaskBoard';
import { TaskFilterBar } from '@/components/raios/tasks/TaskFilterBar';
import { TaskDetailSheet } from '@/components/raios/tasks/TaskDetailSheet';
import { useTasksBoard } from '@/components/raios/tasks/hooks/useTasksBoard';
import { useTasksUrlState } from '@/components/raios/tasks/hooks/useTasksUrlState';
import {
  ISSUE_STATUSES,
  TASK_STATUSES,
  type TaskStatus,
  type TaskUI,
} from '@/components/raios/tasks/types';

export function MCTasksPage() {
  const { tenant } = useAuth();
  const { filters, setFilters, clearFilters, selectedTaskId, setSelectedTaskId } = useTasksUrlState();

  const tenantId = tenant?.id ?? null;
  const board = useTasksBoard({ tenantId, filters });

  const issuesFiltered = useMemo(
    () => ISSUE_STATUSES.reduce((acc, s) => acc + (board.filteredTotals[s] ?? 0), 0),
    [board.filteredTotals],
  );
  const issuesAbsolute = useMemo(
    () => ISSUE_STATUSES.reduce((acc, s) => acc + (board.totals[s] ?? 0), 0),
    [board.totals],
  );

  const initialTask = useMemo<TaskUI | null>(() => {
    if (!selectedTaskId) return null;
    for (const s of TASK_STATUSES) {
      const found = board.byStatus[s].find((t) => t.id === selectedTaskId);
      if (found) return found;
    }
    return null;
  }, [board.byStatus, selectedTaskId]);

  const handleTaskClick = useCallback(
    (task: TaskUI) => setSelectedTaskId(task.id),
    [setSelectedTaskId],
  );

  const handleTransition = useCallback(async (taskId: string, nextStatus: TaskStatus) => {
    try {
      await api.transitionTask(taskId, nextStatus);
    } catch (err) {
      console.error('Falha ao transicionar task', err);
    }
  }, []);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="p-4 md:p-6 space-y-5 pt-12">
        <PageHeader
          title="Tarefas"
          actions={
            <Button variant="ghost" size="sm" onClick={board.refetch} aria-label="Recarregar">
              <RefreshCcw className="h-4 w-4" />
              Recarregar
            </Button>
          }
        />

        {board.isLoading ? (
          <PageSkeleton />
        ) : board.error ? (
          <div className="text-raios-danger-text text-sm border border-raios-danger/30 rounded-md p-3 bg-raios-danger/5 flex items-center justify-between">
            <span>{board.error}</span>
            <Button variant="ghost" size="sm" onClick={board.refetch}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <KpiCount
                label="Pendente"
                value={board.filteredTotals.pending}
                icon={ListChecks}
              />
              <KpiCount
                label="Em execução"
                value={board.filteredTotals.running}
                icon={Clock}
              />
              <KpiCount
                label="Concluído"
                value={board.filteredTotals.done}
                absolute={board.totals.done}
                periodLabel={periodLabelFor(filters.donePeriod)}
                icon={CheckCircle2}
              />
              <KpiCount
                label="Issues"
                value={issuesFiltered}
                absolute={issuesAbsolute}
                periodLabel={periodLabelFor(filters.donePeriod)}
                icon={AlertOctagon}
              />
            </div>

            <TaskFilterBar
              filters={filters}
              knownHandlers={board.knownHandlers}
              targetsByHandler={board.targetsByHandler}
              onChange={setFilters}
              onClear={clearFilters}
            />

            <TaskBoard
              byStatus={board.byStatus}
              totals={board.filteredTotals}
              absoluteTotals={board.totals}
              onTaskClick={handleTaskClick}
            />
          </>
        )}
      </div>

      <TaskDetailSheet
        taskId={selectedTaskId}
        initialTask={initialTask}
        onClose={() => setSelectedTaskId(null)}
        onTransition={handleTransition}
      />
    </TooltipProvider>
  );
}

interface KpiCountProps {
  label: string;
  value: number;
  /** Total absoluto do DB. Quando informado E difere de value, mostra "de N total". */
  absolute?: number;
  /** Rótulo do filtro temporal (ex: "nas últimas 24h"). Quando informado, aparece embaixo. */
  periodLabel?: string;
  icon: LucideIcon;
}

function KpiCount({ label, value, absolute, periodLabel, icon }: KpiCountProps) {
  const showCompare = absolute !== undefined && value !== absolute;
  return (
    <KPICard label={label} value={value} icon={icon}>
      {(periodLabel || showCompare) && (
        <div className="mt-1 space-y-0.5">
          {periodLabel && (
            <p className="text-[11px] text-raios-text-secondary">{periodLabel}</p>
          )}
          {showCompare && (
            <p className="text-[11px] text-raios-muted tabular-nums">
              de {absolute} total
            </p>
          )}
        </div>
      )}
    </KPICard>
  );
}

/**
 * Texto humano do filtro temporal aplicado em done. Vazio quando 'all'
 * (não há filtro temporal pra anunciar).
 */
function periodLabelFor(period: '24h' | '7d' | '30d' | 'all'): string {
  switch (period) {
    case '24h':
      return 'nas últimas 24h';
    case '7d':
      return 'nos últimos 7 dias';
    case '30d':
      return 'nos últimos 30 dias';
    case 'all':
      return '';
  }
}

function PageSkeleton() {
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-9 w-full" />
      <div className="flex gap-4 px-2 py-2 overflow-x-auto">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="w-[280px] flex flex-col gap-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ))}
      </div>
    </>
  );
}

export default MCTasksPage;
